/* Budget and hardware conditions stay combined; product data comes from Shopify. */
(() => {
  'use strict';
  const ranges = { '10': [100000, 150000], '15': [150000, 200000], '20': [200000, 250000], '25': [250000, 300000], '30': [300000, Infinity], all: [0, Infinity] };
  const labels = { gpu: 'GPU', ram: 'メモリ', cpu: 'CPU', ssd: 'SSD', case: 'ケース', color: '色' };
  const money = new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY', maximumFractionDigits: 0 });
  const naturalSort = new Intl.Collator('ja', { numeric: true });

  function specs(title) {
    const gpu = title.match(/\b(RTX|GTX|RX|Arc)\s*([A-Z]?\d{3,4})(?:\s*(Ti\s*SUPER|XTX|XT|GRE|Ti|SUPER))?(?:\s+(\d+)\s*GB)?/i);
    const cpu = title.match(/Ryzen\s+\d\s+\d{4}\w*/i) || title.match(/Core\s*(?:Ultra\s*\d\s+\d{3}\w*|i\d[\s-]\d{4,5}\w*)/i);
    // DDR identifies system memory; the GPU's VRAM must never count as RAM.
    const ram = title.match(/DDR[345]\s*(\d+)\s*GB/i) || title.match(/メモリ\s*(\d+)\s*GB/i);
    const ssd = title.match(/(?:NVMe\s+)?SSD\s*(\d+(?:\.\d+)?\s*[GT]B)/i);
    const caseName = ['AQUA UNO', 'MONTECH XR', 'NeoPrism', 'DS900', 'H17'].find(name => title.includes(name)) || '';
    const tail = caseName ? title.slice(title.lastIndexOf(caseName) + caseName.length) : '';
    const color = ['ホワイトイエロー', 'ブラックイエロー', 'ブラックホワイト', 'ブラック', 'ホワイト'].find(value => tail.includes(value)) || (/\bBK\b/i.test(tail) ? 'ブラック' : /\bWH\b/i.test(tail) ? 'ホワイト' : caseName === 'H17' ? 'ブラック' : '');
    return {
      gpu: gpu ? [gpu[1].toUpperCase(), gpu[2].toUpperCase(), gpu[3]?.toUpperCase().replace(/TI/, 'Ti').replace(/\s+/g, ' '), gpu[4] && gpu[4] + 'GB'].filter(Boolean).join(' ') : '',
      cpu: cpu ? cpu[0].replace(/\s+/g, ' ').trim() : '',
      ram: ram ? ram[1] + 'GB' : '',
      ssd: ssd ? ssd[1].replace(/\s+/g, '').toUpperCase() : '',
      case: caseName, color
    };
  }

  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  function start(root) {
    if (root.dataset.budgetReady) return;
    root.dataset.budgetReady = 'true';
    const query = selector => root.querySelector(selector);
    const selects = [...root.querySelectorAll('[data-budget-filter]')];
    const buttons = [...root.querySelectorAll('[data-budget]')];
    const grid = query('[data-budget-grid]'), count = query('[data-budget-count]'), feedback = query('[data-budget-feedback]');
    const results = query('[data-budget-results]'), more = query('[data-budget-more]'), retry = query('[data-budget-retry]');
    const chips = query('[data-budget-chips]'), reset = query('[data-budget-reset]');
    const params = new URLSearchParams(location.search);
    // Case pages lock the case and start from every price; the budget page starts at 15.
    const lockedCase = (root.dataset.lockedCase || '').trim();
    const fallback = lockedCase ? 'all' : '15';
    const axes = Object.keys(labels).filter(key => !(lockedCase && key === 'case'));
    let budget = params.get('tier') || location.hash.replace('#tier-', '') || fallback;
    if (!ranges[budget]) budget = fallback;
    const state = Object.fromEntries(Object.keys(labels).map(key => [key, params.get(key) || '']));
    if (state.color === 'BK') state.color = 'ブラック';
    if (state.color === 'WH') state.color = 'ホワイト';
    if (lockedCase) state.case = lockedCase;
    let products = [], matched = [], limit = 0, busy = false, failed = false, requestVersion = 0;
    const cache = new Map();
    const pageSize = () => matchMedia('(max-width:768px)').matches ? 6 : 8;
    const matches = (product, except) => Object.keys(state).every(key => key === except || !state[key] || product.specs[key] === state[key]);

    function updateURL() {
      const url = new URL(location.href);
      Object.keys(state).forEach(key => { if (state[key] && axes.includes(key)) url.searchParams.set(key, state[key]); else url.searchParams.delete(key); });
      if (lockedCase) {
        // Keep in-page anchors such as #case-models intact on case pages.
        if (budget === fallback) url.searchParams.delete('tier'); else url.searchParams.set('tier', budget);
      } else {
        url.searchParams.delete('tier');
        url.hash = 'tier-' + budget;
      }
      history.replaceState(null, '', url);
    }

    async function fetchProducts(key) {
      // One query stops at Shopify's 1,000-result search limit, so every price reads band by band.
      if (key === 'all' && !lockedCase) {
        const bands = await Promise.all(['under10', '10', '15', '20', '25', '30'].map(band => fetchProducts(band)));
        return bands.flat().sort((a, b) => a.price - b.price || a.id - b.id);
      }
      const [min, max] = key === 'under10' ? [0, 100000] : ranges[key];
      const url = new URL('/search', location.origin);
      url.searchParams.set('view', 'budget-data');
      url.searchParams.set('type', 'product');
      // A case-name query keeps each case page below Shopify's 1,000-result search limit.
      url.searchParams.set('q', 'tag:オーダーメイドゲーミングPC' + (lockedCase ? ' AND title:"' + lockedCase + '"' : ''));
      url.searchParams.set('sort_by', 'price-ascending');
      url.searchParams.set('filter.v.availability', '1');
      if (min) url.searchParams.set('filter.v.price.gte', min);
      if (Number.isFinite(max)) url.searchParams.set('filter.v.price.lte', max - 1);
      async function read(page) {
        const request = new URL(url);
        request.searchParams.set('page', page);
        const response = await fetch(request, { signal: AbortSignal.timeout(20000) });
        if (!response.ok) throw new Error('Product response ' + response.status);
        const data = await response.json();
        if (!Array.isArray(data.items) || !Number.isInteger(data.pages) || data.page !== page) throw new Error('Invalid product response');
        return data;
      }
      const first = await read(1);
      const all = [...first.items];
      // Read every page, in bounded batches. The old five-page cap omitted matches.
      for (let page = 2; page <= first.pages; page += 3) {
        const pages = await Promise.all(Array.from({ length: Math.min(3, first.pages - page + 1) }, (_, i) => read(page + i)));
        pages.forEach(data => all.push(...data.items));
      }
      const unique = new Map();
      all.forEach(product => {
        if (product.available !== true) return;
        const price = product.price / 100;
        if (!Number.isFinite(product.price) || price < min || price >= max) return;
        if (!product.title.includes('【新品】')) return;
        if (!product.url.startsWith('/products/')) return;
        const productSpecs = specs(product.title);
        if (lockedCase && productSpecs.case !== lockedCase) return;
        unique.set(product.id, { ...product, specs: productSpecs });
      });
      return [...unique.values()].sort((a, b) => a.price - b.price || a.id - b.id);
    }

    function renderControls() {
      selects.forEach(select => {
        const axis = select.dataset.budgetFilter;
        const values = [...new Set(products.map(product => product.specs[axis]).filter(Boolean))];
        if (state[axis] && !values.includes(state[axis])) values.push(state[axis]);
        values.sort(naturalSort.compare);
        select.replaceChildren(new Option('指定なし', ''));
        values.forEach(value => {
          const hits = products.filter(product => product.specs[axis] === value && matches(product, axis)).length;
          const option = new Option(value + '（' + hits + '件）', value);
          option.disabled = hits === 0 && value !== state[axis];
          select.append(option);
        });
        select.value = state[axis];
        select.disabled = busy || failed;
      });
      chips.replaceChildren();
      axes.forEach(axis => {
        if (!state[axis]) return;
        const chip = element('button', 'tg-budget__chip', labels[axis] + '：' + state[axis] + ' ×');
        chip.type = 'button';
        chip.setAttribute('aria-label', labels[axis] + 'の条件「' + state[axis] + '」を解除');
        chip.addEventListener('click', () => {
          state[axis] = ''; apply();
          const select = selects.find(control => control.dataset.budgetFilter === axis);
          const details = select.closest('details');
          if (details) details.open = true;
          select.focus();
        });
        chips.append(chip);
      });
      reset.hidden = !axes.some(axis => state[axis]);
    }

    function card(product) {
      const link = element('a', 'result-card bp-card');
      link.href = product.url;
      link.dataset.productId = product.id;
      link.dataset.price = product.price;
      link.dataset.productTitle = product.title;
      const imageWrap = element('div', 'result-img-wrapper');
      if (product.image) {
        const image = element('img');
        image.src = product.image;
        image.alt = product.specs.case || product.title;
        image.loading = 'lazy'; image.width = 600; image.height = 600;
        imageWrap.append(image);
      }
      if (!product.available) imageWrap.append(element('span', 'bp-card__sold', '売り切れ'));
      const info = element('div', 'result-info');
      info.append(element('div', 'result-title', product.specs.gpu ? product.specs.gpu + '搭載｜' + (product.specs.case || 'ゲーミングPC') : product.title));
      const summary = element('p', 'bp-card__summary');
      [product.specs.cpu, product.specs.ram && 'メモリ ' + product.specs.ram, product.specs.ssd && 'SSD ' + product.specs.ssd, product.specs.color].filter(Boolean).forEach(text => summary.append(element('span', '', text)));
      info.append(summary);
      const price = element('div', 'result-price');
      price.append(element('span', 'price-regular', money.format(product.price / 100)), element('span', 'tg-budget__tax', '（税込）'));
      info.append(price);
      link.append(imageWrap, info);
      return link;
    }

    function renderResults(append = false) {
      const start = append ? grid.children.length : 0;
      if (!append) grid.replaceChildren();
      matched.slice(start, limit).forEach(product => grid.append(card(product)));
      const visible = Math.min(limit, matched.length);
      count.textContent = matched.length + '件' + (matched.length ? ' ／ ' + visible + '件を表示・価格の安い順' : '');
      feedback.hidden = matched.length > 0;
      feedback.textContent = 'この予算と条件に合うモデルはありません。条件を解除するか、予算を変更してください。';
      more.hidden = visible >= matched.length;
      more.textContent = 'さらに' + Math.min(pageSize(), matched.length - visible) + '件を見る（残り' + (matched.length - visible) + '件）';
    }

    function apply() {
      updateURL();
      renderControls();
      if (busy || failed) return;
      matched = products.filter(product => matches(product));
      limit = pageSize();
      renderResults();
    }

    async function loadBudget() {
      const version = ++requestVersion, key = budget;
      busy = true; failed = false;
      products = []; matched = [];
      buttons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.budget === budget)));
      selects.forEach(select => { select.disabled = true; });
      results.setAttribute('aria-busy', 'true');
      grid.replaceChildren(); more.hidden = true; feedback.hidden = true; retry.hidden = true;
      count.textContent = '商品を読み込み中…';
      updateURL();
      try {
        if (!cache.has(key)) cache.set(key, fetchProducts(key).catch(error => { cache.delete(key); throw error; }));
        const data = await cache.get(key);
        if (version !== requestVersion) return;
        products = data;
        busy = false;
        results.setAttribute('aria-busy', 'false');
        apply();
      } catch (error) {
        if (version !== requestVersion) return;
        busy = false; failed = true;
        results.setAttribute('aria-busy', 'false');
        count.textContent = '商品を読み込めませんでした';
        feedback.textContent = '通信状況を確認して、再読み込みしてください。';
        feedback.hidden = false; retry.hidden = false;
      }
    }

    buttons.forEach(button => button.addEventListener('click', () => { budget = button.dataset.budget; loadBudget(); }));
    selects.forEach(select => select.addEventListener('change', () => { state[select.dataset.budgetFilter] = select.value; apply(); }));
    reset.addEventListener('click', () => { axes.forEach(key => { state[key] = ''; }); apply(); selects[0].focus(); });
    retry.addEventListener('click', loadBudget);
    more.addEventListener('click', () => { const previous = grid.children.length; limit += pageSize(); renderResults(true); grid.children[previous]?.focus(); });
    window.addEventListener('hashchange', () => { const key = location.hash.replace('#tier-', ''); if (ranges[key] && key !== budget) { budget = key; loadBudget(); } });
    renderControls();
    loadBudget();
  }
  document.querySelectorAll('[data-budget-picker]').forEach(start);
})();
